import { PaginatorState } from "src/app/shared/components/treeslo-grid/treeslo-paginator-cursor-based/treeslo-paginator-cursor-based.component";
import { TreesloGridSortSpec } from "src/app/shared/components/treeslo-grid/treeslo-grid.model";
import { serializeSortForQuery } from "src/app/shared/components/treeslo-grid/helpers/treeslo-grid-sort.helper";
import { StoreService } from "src/app/services/apps/store/store.service";
import { StoreGetDTO } from "../stores/store";

export const DEFAULT_CASE_LIST_PAGE_SIZE = 15;
export const CASE_LIST_PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 30];

export interface CaseListFilter {
  caseId: string;
  provider: string;
  storeId: string;
  claimType: string;
  status: string;
  startFiledDate: string;
  endFiledDate: string;
}

/** Reimbursement ledger filter for GET /cases/search (`reimbursement` query param). */
export type CaseReimbursementFilterValue = "all" | "with" | "without";

export interface CaseRouteFilterForm extends CaseListFilter {
  reimbursementFilter?: CaseReimbursementFilterValue;
}

export function toCaseListApiFilter(
  filterForm: CaseRouteFilterForm | CaseListFilter,
): CaseListFilter {
  return {
    caseId: filterForm.caseId ?? "",
    provider: filterForm.provider ?? "",
    storeId: filterForm.storeId ?? "",
    claimType: filterForm.claimType ?? "",
    status: filterForm.status ?? "",
    startFiledDate: filterForm.startFiledDate ?? "",
    endFiledDate: filterForm.endFiledDate ?? "",
  };
}

export interface CaseListQueryInput {
  filter: CaseListFilter;
  reimbursementFilter?: CaseReimbursementFilterValue;
  pageSize: number;
  cursor: string | null;
  stores: StoreGetDTO[];
  applySingleStoreDefault: boolean;
  sort?: TreesloGridSortSpec[];
  formatDate?: (value: Date | string) => string;
}

export function defaultCaseListFilter(
  startDate: string,
  endDate: string,
): CaseListFilter {
  return {
    caseId: "",
    provider: "",
    storeId: "",
    claimType: "",
    status: "",
    startFiledDate: startDate,
    endFiledDate: endDate,
  };
}

export function buildCaseListQuery(
  input: CaseListQueryInput,
): Record<string, string> {
  const formatDate =
    input.formatDate ??
    ((value: Date | string) =>
      typeof value === "string" ? value : value.toISOString().slice(0, 10));

  const storeId = StoreService.resolveStoreIdWithSingleStoreDefault(
    input.filter.storeId,
    input.stores,
    input.applySingleStoreDefault,
  );

  const params: Record<string, string> = {
    limit: input.pageSize.toString(),
  };

  if (input.filter.caseId) {
    params["caseId"] = input.filter.caseId;
  }
  if (storeId) {
    params["storeId"] = storeId;
  }
  if (input.filter.provider) {
    params["provider"] = input.filter.provider;
  }
  if (input.filter.claimType) {
    params["claimType"] = input.filter.claimType;
  }
  if (input.filter.status) {
    params["status"] = input.filter.status;
  }

  if (
    input.reimbursementFilter === "with" ||
    input.reimbursementFilter === "without"
  ) {
    params["reimbursement"] = input.reimbursementFilter;
  }

  const startDate = input.filter.startFiledDate
    ? formatDate(input.filter.startFiledDate)
    : "";
  const endDate = input.filter.endFiledDate
    ? formatDate(input.filter.endFiledDate)
    : "";

  if (startDate) {
    params["startDate"] = startDate;
  }
  if (endDate) {
    params["endDate"] = endDate;
  }
  if (input.cursor) {
    params["cursor"] = input.cursor;
  }

  const serializedSort = serializeSortForQuery(input.sort ?? []);
  if (serializedSort) {
    params["sort"] = serializedSort;
  }

  return params;
}

export function buildCaseListQueryFromRouteContext(
  contextData:
    | {
        filterForm?: CaseRouteFilterForm;
        paginatorState?: PaginatorState;
        sort?: TreesloGridSortSpec[];
      }
    | undefined,
  stores: StoreGetDTO[],
  defaultRange: { startDate: string; endDate: string },
): Record<string, string> {
  const filter = toCaseListApiFilter(
    contextData?.filterForm ??
      defaultCaseListFilter(defaultRange.startDate, defaultRange.endDate),
  );
  const reimbursementFilter = (
    contextData?.filterForm as CaseRouteFilterForm | undefined
  )?.reimbursementFilter;

  const paginator: PaginatorState = contextData?.paginatorState ?? {
    pageIndex: 0,
    pageSize: DEFAULT_CASE_LIST_PAGE_SIZE,
    cursors: [null],
  };

  const cursor = paginator.cursors[paginator.pageIndex] ?? null;

  return buildCaseListQuery({
    filter,
    reimbursementFilter,
    pageSize: paginator.pageSize || DEFAULT_CASE_LIST_PAGE_SIZE,
    cursor,
    stores,
    applySingleStoreDefault: !contextData?.filterForm,
    sort: contextData?.sort,
  });
}
